<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="h-full">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            Quick Activation
                        </h3>
                    </div>
                </template>
                <div v-if="result" class="space-y-4">
                    <UAlert
                        :title="result.activated ? 'Membership activated' : 'Activation failed'"
                        :description="result.message"
                        :icon="result.activated ? 'i-ph-check-circle-duotone' : 'i-ph-x-circle-duotone'"
                        :color="result.activated ? 'green' : 'red'"
                        variant="subtle"
                    />
                </div>
                <div v-else class="space-y-6">
                    <UFormGroup v-if="partners.length > 1" name="partner_id" label="Partner" required>
                        <USelectMenu
                            v-model="state.partner_id"
                            :options="partners"
                            value-attribute="id"
                            option-attribute="name"
                            size="xl"
                        />
                    </UFormGroup>
                    <div class="grid grid-cols-3 gap-3">
                        <UFormGroup name="first_name" label="First Name" required>
                            <UInput v-model="state.first_name" size="xl" />
                        </UFormGroup>
                        <UFormGroup name="middle_name" label="Middle Name">
                            <UInput v-model="state.middle_name" size="xl" />
                        </UFormGroup>
                        <UFormGroup name="last_name" label="Last Name" required>
                            <UInput v-model="state.last_name" size="xl" />
                        </UFormGroup>
                    </div>
                    <UFormGroup name="phone" label="Phone Number" required help="The welcome message is sent to this number on WhatsApp.">
                        <InputPhone v-model="state.phone" />
                    </UFormGroup>
                    <UFormGroup name="birth_date" label="Date of Birth" required>
                        <UInput v-model="state.birth_date" type="date" size="xl" />
                    </UFormGroup>
                    <UFormGroup
                        v-for="field in recordFields"
                        :key="field.key"
                        :name="`fields.${field.key}`"
                        :label="field.label"
                        required
                    >
                        <UInput v-model="state.fields[field.key]" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="expires_at" label="Expiry Date" required help="The membership stays active until this date.">
                        <UInput v-model="state.expires_at" type="date" size="xl" :min="tomorrow" />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <template v-if="result">
                            <UButton @click="dismiss(true)" size="lg" color="white">
                                Close
                            </UButton>
                            <UButton @click="reset()" size="lg" color="black">
                                Activate Another
                            </UButton>
                        </template>
                        <template v-else>
                            <UButton @click="dismiss()" size="lg" color="white">
                                Cancel
                            </UButton>
                            <UButton type="submit" size="lg" :loading="loading" color="black">
                                Activate
                            </UButton>
                        </template>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { Partner } from '~/types/partner'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    partners: Pick<Partner, 'id' | 'name' | 'metadata'>[]
}>()

const client = useSupabaseClient()

const loading = ref<boolean>(false)
const result = ref<{ activated: boolean; message: string }>()
const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD')

const state = ref<{
    partner_id?: string
    first_name?: string
    middle_name?: string
    last_name?: string
    phone?: string
    birth_date?: string
    expires_at?: string
    fields: Record<string, string>
}>({ fields: {} })

const selectedPartner = computed(() => props.partners.find(p => p.id === state.value.partner_id))

// Birth year is derived from the date of birth, as in bulk activation.
const recordFields = computed(() => (selectedPartner.value?.metadata?.subscription_fields ?? [])
    .filter(f => f.required && f.key !== 'birth_year'))

const schema = z.object({
    partner_id: z.string().min(1, 'Required'),
    first_name: z.string().trim().min(1, 'Required'),
    middle_name: z.string().trim().optional(),
    last_name: z.string().trim().min(1, 'Required'),
    phone: z.string().refine((phone) => {
        const parsed = usePhone().parse(phone)
        return !!parsed?.isValid() && ['MOBILE', 'FIXED_LINE_OR_MOBILE'].includes(parsed.getType() ?? '')
    }, 'Enter a valid mobile number'),
    birth_date: z.string().refine(d => dayjs(d, 'YYYY-MM-DD', true).isValid() && dayjs(d).isBefore(dayjs()), 'Enter a valid date of birth'),
    expires_at: z.string().refine(d => dayjs(d, 'YYYY-MM-DD', true).isValid() && dayjs(d).isAfter(dayjs(), 'day'), 'Must be a future date'),
    fields: z.record(z.string()),
}).superRefine((data, ctx) => {
    recordFields.value.forEach((field) => {
        if (!data.fields[field.key]?.trim()) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['fields', field.key] })
        }
    })
})

watch(() => props.open, (open) => {
    if (open) {
        reset()
    }
})

function reset() {
    result.value = undefined
    state.value = {
        partner_id: props.partners[0]?.id,
        fields: {},
    }
}

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    loading.value = true

    const { birth_date, fields } = event.data
    const credentialFields = (selectedPartner.value?.metadata?.subscription_fields ?? [])
        .filter(f => f.required)
        .reduce((acc, { key }) => {
            acc[key] = key === 'birth_year'
                ? dayjs(birth_date).year().toString()
                : fields[key].trim()
            return acc
        }, {} as Record<string, string>)

    const { data, error } = await client.functions.invoke('bulk-activation', {
        body: {
            partnerId: event.data.partner_id,
            name: 'Quick activation',
            identityType: 'phone',
            createCredentials: true,
            data: [{
                firstName: event.data.first_name,
                middleName: event.data.middle_name || null,
                lastName: event.data.last_name,
                phone: event.data.phone.replaceAll('+', ''),
                birthDate: birth_date,
                expiresAt: event.data.expires_at,
                credentialFields,
            }],
        },
    })

    loading.value = false

    if (error || !data?.summary) {
        const message = error?.context ? (await error.context.json().catch(() => null))?.error : undefined
        result.value = { activated: false, message: message || 'Something went wrong. Try again.' }
        return
    }

    if (data.errors?.length) {
        result.value = { activated: false, message: data.errors[0].message }
        return
    }

    result.value = {
        activated: true,
        message: data.summary.whatsapp_messages_sent
            ? `${event.data.first_name} is covered until ${dayjs(event.data.expires_at).format('DD MMM YYYY')}. The welcome message was sent on WhatsApp.`
            : `${event.data.first_name} is covered until ${dayjs(event.data.expires_at).format('DD MMM YYYY')}. The WhatsApp message could not be sent; resend it from the activations list.`,
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)
    if (reload) {
        emit('reload', true)
    }
}
</script>
